/**
 * LexDuo · F2 · Histórico de emails enviados (tab "Emails" del detalle)
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Mail, Loader2, Eye, RefreshCw, AlertCircle } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { obtenerToken } from '../../lib/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const headers = () => {
  const tok = obtenerToken();
  return tok ? { Authorization: `Bearer ${tok}` } : {};
};

const EstadoBadge = ({ estado }) => {
  const map = {
    pendiente:  { color: 'bg-gray-200 text-gray-700', label: 'Pendiente' },
    enviado:    { color: 'bg-emerald-100 text-emerald-700', label: 'Enviado' },
    error:      { color: 'bg-red-100 text-red-700', label: 'Error' },
    respondido: { color: 'bg-blue-100 text-blue-700', label: 'Respondido' },
  };
  const c = map[estado] || { color: 'bg-gray-100', label: estado };
  return <Badge className={`${c.color} hover:${c.color}`}>{c.label}</Badge>;
};

const EmailsLog = ({ ejecucionId }) => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [emailOpen, setEmailOpen] = useState(null);

  const cargar = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/ejecuciones/${ejecucionId}/emails`, { headers: headers() });
      setItems(r.data?.items || []);
    } catch (e) {
      toast({ title: 'Error', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setLoading(false); }
  };

  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, [ejecucionId]);

  const verDetalle = async (id) => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/ejecuciones/${ejecucionId}/emails/${id}`, { headers: headers() });
      setEmailOpen(r.data);
    } catch (e) {
      toast({ title: 'Error', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-base flex items-center gap-2"><Mail className="w-5 h-5 text-violet-700" /> Histórico de emails</CardTitle>
          <CardDescription>Todos los emails enviados a procurador y cliente</CardDescription>
        </div>
        <Button variant="ghost" size="sm" onClick={cargar} data-testid="btn-refresh-emails">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Loader2 className="w-6 h-6 animate-spin text-violet-700 mx-auto" />
        ) : items.length === 0 ? (
          <div className="text-sm text-gray-500 text-center py-6">
            <Mail className="w-10 h-10 mx-auto text-gray-300 mb-2" />
            Aún no se ha enviado ningún email.
          </div>
        ) : (
          <div className="space-y-2">
            {items.map(x => (
              <div key={x.id} className="border rounded-lg p-3 hover:bg-gray-50" data-testid={`email-row-${x.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className="capitalize">{x.destinatario_tipo}</Badge>
                      <EstadoBadge estado={x.estado} />
                      <span className="text-sm text-gray-700 font-medium">{x.destinatario_email}</span>
                    </div>
                    <div className="text-sm font-medium mt-1 truncate">{x.asunto}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      {x.enviado_at ? `Enviado: ${new Date(x.enviado_at).toLocaleString('es-ES')}` : `Creado: ${new Date(x.created_at).toLocaleString('es-ES')}`}
                    </div>
                    {x.error_msg && (
                      <div className="text-xs text-red-600 mt-1 flex items-start gap-1">
                        <AlertCircle className="w-3 h-3 mt-0.5" /> {x.error_msg}
                      </div>
                    )}
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => verDetalle(x.id)} data-testid={`btn-ver-email-${x.id}`}>
                    <Eye className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {emailOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setEmailOpen(null)}>
          <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b">
              <div className="font-semibold">{emailOpen.asunto}</div>
              <div className="text-xs text-gray-500">A {emailOpen.destinatario_email} · {emailOpen.estado}</div>
            </div>
            <div className="p-4">
              <div dangerouslySetInnerHTML={{ __html: emailOpen.cuerpo }} />
            </div>
            <div className="p-4 border-t flex justify-end">
              <Button variant="outline" onClick={() => setEmailOpen(null)}>Cerrar</Button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

export default EmailsLog;
